import dayjs from "./dayjs";
import _dstCalculator from "dst-calculator";
import { HOURS } from "../constants";

interface IDaylightTransition {
    day: number;
    dayOfWeek: string;
    month: number;
    timeOfDay: string;
    week: number;
    isFixedDateRule?: boolean;
}

interface IAdjustmentRule {
    baseUtcOffsetDelta?: string;
    dateEnd: string;
    dateStart: string;
    daylightDelta: string;
    daylightTransitionEnd: IDaylightTransition;
    daylightTransitionStart: IDaylightTransition;
}

export interface ITimeZoneInfoDetails {
    id: string;
    isDaylightSavingTimeSupported: boolean;
    offsetFromUtc: string;
}

export interface ITimeZoneInfo {
    adjustmentRules: IAdjustmentRule[];
    timeZoneInfo: ITimeZoneInfoDetails;
    timeZoneId: string;
}

export interface IReportPeriod {
    fromDate: Date;
    toDate: Date;
}

export const dstCalculator = _dstCalculator();

export function changeTimeZoneForDstCalculator(timeZoneId: string, timeZoneInfo: ITimeZoneInfo[]) {
    dstCalculator.initialize(undefined, undefined, timeZoneId, timeZoneInfo);
}

export function addTimeZone(timeZoneInfo): void {
    dstCalculator.addTimeZone(timeZoneInfo);
}

/**
 * Returns midnight time of the provided date
 * @param date The provided date
 * @returns Midnight time of the provided date
 */
export function getStartOfDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
}

/**
 * Builds the `YYYYMMDD` key (in the user's timezone) used to index cached historical days in
 * `historicalDayTracker`. The add-log flow, {@link HistoricalDayLoader} and the logs page must all
 * derive the key the same way so a day that is added, invalidated, and re-loaded resolves to the
 * same cache entry.
 */
export function makeHistoricalDayKey(day: Date): string {
    const local = dstCalculator.toLocalDateTime(day);
    const m = local.getMonth() + 1;
    const d = local.getDate();
    return `${ local.getFullYear() }${ m < 10 ? "0" : "" }${ m }${ d < 10 ? "0" : "" }${ d }`;
}

export function getStartOfDayUserTimezone(date: Date, timeZoneId?: string): Date {
    let localDate: Date = dstCalculator.toLocalDateTime(date, timeZoneId);

    // Correct localDate according to device dst shift
    let localDatePlusHour = new Date(localDate.getTime() + 60 * 60 * 1000);
    let deviceLocalDatePlusHourOffset = localDatePlusHour.getTimezoneOffset() - localDate.getTimezoneOffset();
    localDate.setMinutes(localDate.getMinutes() + deviceLocalDatePlusHourOffset);

    // get day start
    let localDayStart = getStartOfDay(localDate);
    let dayStart: Date = dstCalculator.toReverseOffsetDateTime(localDayStart, timeZoneId);

    // Add user's timezone offset difference as well as device timezone shift for the target date
    let plusHour = new Date(dayStart.getTime());
    plusHour.setUTCMinutes(plusHour.getUTCMinutes() + 60);
    let plusHourOffset = dstCalculator.getOffset(plusHour, timeZoneId) - dstCalculator.getOffset(dayStart, timeZoneId);
    let localPlusHourOffset = plusHour.getTimezoneOffset() - dayStart.getTimezoneOffset();
    dayStart.setUTCMinutes(dayStart.getUTCMinutes() + plusHourOffset - localPlusHourOffset);
    return dayStart;
}

export function getPreviousDayStart (date: Date, isAlreadyStartOfDay = false) {
    let currentDayStart = isAlreadyStartOfDay ? new Date(date) : getStartOfDayUserTimezone(date);
    // Subtract 6 hours guarantees us that we'll get previous day (do not need to take into account DST rules)
    currentDayStart.setUTCHours(currentDayStart.getUTCHours() - 6);
    let previousDayStart = getStartOfDayUserTimezone(currentDayStart);

    if (date.getTime() - previousDayStart.getTime() > 30 * HOURS) {
        // Gap too big, must have skipped a day due to DST related bug caused by calling getStartOfDayUserTimezone on a value that is already the start of day.
        // Since we got the start of day for the wrong day, add another day and go forward.
        previousDayStart.setUTCHours(previousDayStart.getUTCHours() + 30);
        return getStartOfDayUserTimezone(previousDayStart);
    }
    return previousDayStart;
}

export function getNextDayStart (date: Date, isAlreadyStartOfDay = false) {
    let currentDayStart = isAlreadyStartOfDay ? new Date(date) : getStartOfDayUserTimezone(date);
    // Adding 30 hours guarantees us that we'll get next day (do not need to take into account DST rules)
    currentDayStart.setUTCHours(currentDayStart.getUTCHours() + 30);
    let nextDayStart = getStartOfDayUserTimezone(currentDayStart);

    if (nextDayStart.getTime() === date.getTime()) {
        // Ended up getting the same value? DST related bug caused by calling getStartOfDayUserTimezone on a value that is already the start of day.
        // Since we got the start of day for the previous day instead, add another day and go forward.
        nextDayStart.setUTCHours(nextDayStart.getUTCHours() + 30);
        return getStartOfDayUserTimezone(nextDayStart);
    }
    return nextDayStart;
}

export function compareDates(dateA: Date | number | string, dateB: Date | number | string) {
    let a = new Date(dateA as string);
    let b = new Date(dateB as string);
    return a.getTime() === b.getTime()
        ? 0
        : (a.getTime() > b.getTime() ? -1 : 1);
}

export function clone (date: Date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds());
}

/**
 * A standardized format of a local date key string. In the format "YYYY-MM-DD"
 */
export function getDateTokenString(date: Date, isLocal = false): string {
    let thisDate = date;
    if (isLocal) {
        thisDate = dstCalculator.toLocalDateTime(date);
    }
    return `${ thisDate.getFullYear() }-${ thisDate.getMonth() + 1 }-${ thisDate.getDate() }`;
}

// Returns start and end of day dates shifted according to user settings timezone
export function getLocalDaySpan(deviceDate: Date): IReportPeriod {
    let localDate: Date = dstCalculator.toLocalDateTime(deviceDate);
    let startOfDay = getStartOfDay(localDate);
    let fromDate: Date = dstCalculator.toReverseOffsetDateTime(startOfDay);
    let toDate = dstCalculator.toReverseOffsetDateTime(new Date(startOfDay.getFullYear(), startOfDay.getMonth(), startOfDay.getDate() + 1, 0, 0, 0));

    return { fromDate, toDate };
}

export function formatLocalDate (dateTime: Date | string, format = "YYYY-MM-DD HH:mm") {
    return dayjs(dstCalculator.toLocalDateTime(dateTime)).format(format);
}

export function toDateTime(dateTime: Date | string) {
    return typeof (dateTime) === "string" ? new Date(dateTime) : new Date(dateTime.getTime());
}

export function msToDurationString(ms: number | string) {
    const milliseconds = typeof ms === "string" ? parseInt(ms, 10) : ms;

    // Convert milliseconds to a Date object
    const date = new Date(milliseconds);

    // Extract the components of the date
    const days = Math.floor(milliseconds / 86400000);
    const hours = date.getUTCHours();
    const minutes = date.getUTCMinutes();
    const seconds = date.getUTCSeconds();

    // Format the components into a string
    const dateString: string[] = [];
    if (days > 0) {
        dateString.push(`${ days }d`);
    }
    if (hours > 0) {
        dateString.push(`${ hours }h`);
    }
    if (minutes > 0) {
        dateString.push(`${ minutes }m`);
    }
    if (seconds > 0) {
        dateString.push(`${ seconds }s`);
    }

    // Show the ms value if the value is less than 1 second
    if (!dateString.length) {
        dateString.push(`${ milliseconds }ms`);
    }
    return dateString.join("");
}

/**
 * Returns a sort comparator for dates that defaults to chronological
 * @param isChronological A boolean value that determines whether sort is chronological (default true)
 * @returns Comparator predicate that results in sorted list that defaults to chronological
 */
export function dateTimeComparator(isChronological = true) {
    return (a: IDateTimeProvider, b: IDateTimeProvider) => {
        if (typeof (a.dateTime) !== typeof (b.dateTime)) {
            throw new Error("Invalid date sort parameter types");
        }
        let sortValue = a.dateTime > b.dateTime ? 1 : a.dateTime < b.dateTime ? -1 : 0;
        return isChronological ? sortValue : -1 * sortValue;
    };
}

/**
 * Spin-off of dateTimeComparator which accepts undefined value for dateTime
 */
export function optionalDateTimeComparator(isChronological = true) {
    return (a: Partial<IDateTimeProvider>, b: Partial<IDateTimeProvider>) => {
        if (typeof (a.dateTime) !== typeof (b.dateTime)) {
            throw new Error("Invalid date sort parameter types");
        }
        let sortValue = a.dateTime && b.dateTime
                        ? a.dateTime > b.dateTime ? 1 : a.dateTime < b.dateTime ? -1 : 0
                        : 0;
        return isChronological ? sortValue : -1 * sortValue;
    };
}

/**
 * Calculate the number of days from a given date until now
 * @param fromDate The starting date (as Date, string, or number timestamp)
 * @returns The number of days (rounded down) from the given date until now
 */
export function getDaysSince(fromDate: Date | string | number): number {
    const startTime = new Date(fromDate).getTime();
    const now = Date.now();
    const millisecondsDiff = now - startTime;
    const daysDiff = Math.floor(millisecondsDiff / (1000 * 60 * 60 * 24));
    return daysDiff;
}